import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LinkIcon, Plus, Copy, Trash2, Clock, CheckCircle, XCircle,
  Users, Shield, Loader2, Eye, EyeOff, RefreshCw, Building2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'client', label: 'Participant', desc: 'Client / participant in a program' },
  { value: 'peer_specialist', label: 'Peer Support Specialist', desc: 'Assigned peer support worker' },
  { value: 'facilitator', label: 'Facilitator', desc: 'HEAT/HER curriculum facilitator' },
  { value: 'referral_source', label: 'Referral Source', desc: 'External referral agency contact' },
];

const PROGRAM_OPTIONS = [
  { value: '', label: 'None / General' },
  { value: 'purpose_driven', label: 'Purpose Driven' },
  { value: 'heat', label: 'HEAT' },
  { value: 'her', label: 'HER' },
  { value: 'our_square', label: 'Our Square Transitional Living' },
];

const EXPIRY_OPTIONS = [
  { value: '24', label: '24 hours' },
  { value: '48', label: '48 hours' },
  { value: '72', label: '72 hours (default)' },
  { value: '168', label: '1 week' },
  { value: '720', label: '30 days' },
];

const STATUS_CONFIG = {
  active: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle, label: 'Active' },
  exhausted: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Users, label: 'Used' },
  expired: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, label: 'Expired' },
  revoked: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Revoked' },
};

export default function MagicLinksPage() {
  const { token } = useAuth();
  const [links, setLinks] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [revoking, setRevoking] = useState(null);

  const headers = { 'Content-Type': 'application/json' };

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const [linksRes, agenciesRes] = await Promise.all([
        fetch(`${API}/api/magic-links`, { headers, credentials: 'include' }),
        fetch(`${API}/api/agencies`, { headers, credentials: 'include' }),
      ]);
      const [linksData, agenciesData] = await Promise.all([linksRes.json(), agenciesRes.json()]);
      setLinks(Array.isArray(linksData) ? linksData : []);
      setAgencies(Array.isArray(agenciesData) ? agenciesData : []);
    } catch {
      toast.error('Failed to load magic links');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleRevoke = async (linkId) => {
    setRevoking(linkId);
    try {
      const res = await fetch(`${API}/api/magic-links/${linkId}/revoke`, { method: 'PUT', headers, credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Link revoked');
      fetchLinks();
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setRevoking(null);
    }
  };

  const filteredLinks = filter === 'all' ? links : links.filter(l => l.status === filter);

  const counts = {
    all: links.length,
    active: links.filter(l => l.status === 'active').length,
    exhausted: links.filter(l => l.status === 'exhausted').length,
    expired: links.filter(l => l.status === 'expired').length,
    revoked: links.filter(l => l.status === 'revoked').length,
  };

  const agencyMap = {};
  agencies.forEach(a => { agencyMap[a.id] = a.name; });

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="magic-links-loading">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="magic-links-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="magic-links-title">Onboarding Links</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate secure, single-use onboarding links for new users. Links pre-assign roles but do not expand permissions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchLinks} data-testid="refresh-links-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-link-btn">
            <Plus size={14} className="mr-1" /> Create Link
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex gap-2 flex-wrap" data-testid="status-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'exhausted', label: 'Used' },
          { key: 'expired', label: 'Expired' },
          { key: 'revoked', label: 'Revoked' },
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className="gap-1.5"
            data-testid={`filter-${f.key}`}
          >
            {f.label}
            <Badge variant="secondary" className="text-xs ml-1">{counts[f.key]}</Badge>
          </Button>
        ))}
      </div>

      {filteredLinks.length === 0 ? (
        <Card data-testid="no-links-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <LinkIcon className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <h3 className="font-semibold text-base">No onboarding links</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === 'all' ? 'Create your first onboarding link to invite users.' : `No ${filter} links found.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="links-list">
          {filteredLinks.map(link => (
            <LinkCard
              key={link.id}
              link={link}
              agencyMap={agencyMap}
              onRevoke={handleRevoke}
              revoking={revoking === link.id}
            />
          ))}
        </div>
      )}

      <CreateLinkDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        agencies={agencies}
        headers={headers}
        onCreated={() => { setShowCreate(false); fetchLinks(); }}
      />
    </div>
  );
}

function LinkCard({ link, agencyMap, onRevoke, revoking }) {
  const [showToken, setShowToken] = useState(false);
  const statusCfg = STATUS_CONFIG[link.status] || STATUS_CONFIG.active;
  const StatusIcon = statusCfg.icon;
  const roleLabel = ROLE_OPTIONS.find(r => r.value === link.role)?.label || link.role;

  const createdDate = link.created_at ? new Date(link.created_at).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';
  const expiresDate = link.expires_at ? new Date(link.expires_at).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';
  const redeemedDate = link.redeemed_at ? new Date(link.redeemed_at).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  const copyLink = () => {
    const url = `${window.location.origin}/onboard/${link.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard'));
  };

  return (
    <Card className="hover:shadow-sm transition-shadow" data-testid={`link-card-${link.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            {/* Row 1: Status + Role + Agency */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${statusCfg.color} border gap-1`} variant="outline" data-testid="link-status">
                <StatusIcon size={12} /> {statusCfg.label}
              </Badge>
              <Badge variant="secondary" data-testid="link-role">{roleLabel}</Badge>
              {link.program && (
                <Badge variant="outline" className="text-xs">{PROGRAM_OPTIONS.find(p => p.value === link.program)?.label || link.program}</Badge>
              )}
              {link.agency_id && agencyMap[link.agency_id] && (
                <Badge variant="outline" className="gap-1 text-xs border-slate-300">
                  <Building2 size={11} /> {agencyMap[link.agency_id]}
                </Badge>
              )}
            </div>

            {/* Row 2: Created by / prefill / notes */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span>Created by <strong className="text-foreground">{link.created_by_name}</strong></span>
              <span>{createdDate}</span>
              {link.pre_fill_name && <span>For: <strong className="text-foreground">{link.pre_fill_name}</strong></span>}
              {link.pre_fill_email && <span className="text-muted-foreground/70">{link.pre_fill_email}</span>}
            </div>

            {/* Row 3: Expiry */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><Clock size={11} /> Expires: {expiresDate}</span>
              <span>Uses: {link.use_count}/{link.max_uses}</span>
              {link.notes && <span className="italic">"{link.notes}"</span>}
            </div>

            {/* Row 4: Redeemed info */}
            {link.redeemed_by_name && (
              <div className="flex items-center gap-4 text-xs bg-blue-50 rounded-md px-3 py-1.5 border border-blue-100" data-testid="redeemed-info">
                <span className="flex items-center gap-1"><Users size={11} className="text-blue-600" /> Redeemed by <strong>{link.redeemed_by_name}</strong></span>
                {link.redeemed_by_email && <span>{link.redeemed_by_email}</span>}
                <span>{redeemedDate}</span>
              </div>
            )}

            {/* Token display */}
            {showToken && (
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all" data-testid="link-token">
                  {window.location.origin}/onboard/{link.token}
                </code>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowToken(!showToken)} data-testid={`toggle-token-${link.id}`}>
              {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
            </Button>
            {link.status === 'active' && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copyLink} data-testid={`copy-link-${link.id}`}>
                  <Copy size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onRevoke(link.id)}
                  disabled={revoking}
                  data-testid={`revoke-link-${link.id}`}
                >
                  {revoking ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateLinkDialog({ open, onClose, agencies, headers, onCreated }) {
  const [form, setForm] = useState({
    role: '',
    agency_id: '',
    program: '',
    expires_hours: '72',
    pre_fill_name: '',
    pre_fill_email: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);

  const showAgency = form.role === 'referral_source' || form.role === 'client';

  const handleCreate = async () => {
    if (!form.role) { toast.error('Please select a role'); return; }
    setCreating(true);
    try {
      const body = {
        role: form.role,
        expires_hours: parseInt(form.expires_hours),
        max_uses: 1,
        pre_fill_name: form.pre_fill_name,
        pre_fill_email: form.pre_fill_email,
        notes: form.notes,
      };
      if (form.agency_id) body.agency_id = form.agency_id;
      if (form.program) body.program = form.program;

      const res = await fetch(`${API}/api/magic-links/generate`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      const data = await res.json();
      setCreatedLink(data);
      toast.success('Onboarding link created');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setForm({ role: '', agency_id: '', program: '', expires_hours: '72', pre_fill_name: '', pre_fill_email: '', notes: '' });
    setCreatedLink(null);
    onClose();
    if (createdLink) onCreated();
  };

  const copyCreatedLink = () => {
    if (!createdLink) return;
    const url = `${window.location.origin}/onboard/${createdLink.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Copied!'));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="create-link-dialog">
        <DialogHeader>
          <DialogTitle>{createdLink ? 'Link Created' : 'Create Onboarding Link'}</DialogTitle>
        </DialogHeader>

        {createdLink ? (
          <div className="space-y-4" data-testid="created-link-result">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-800 mb-2">Onboarding link is ready. Share it securely.</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white px-3 py-2 rounded border flex-1 break-all font-mono" data-testid="created-link-url">
                  {window.location.origin}/onboard/{createdLink.token}
                </code>
                <Button size="sm" onClick={copyCreatedLink} data-testid="copy-created-link">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Role: <strong>{ROLE_OPTIONS.find(r => r.value === createdLink.role)?.label}</strong></p>
              <p>Single-use. Expires: {new Date(createdLink.expires_at).toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Role */}
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="select-role"><SelectValue placeholder="Select role to assign" /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label} — <span className="text-muted-foreground text-xs">{r.desc}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Program */}
            <div className="space-y-1.5">
              <Label>Program</Label>
              <Select value={form.program} onValueChange={v => setForm(f => ({ ...f, program: v }))}>
                <SelectTrigger data-testid="select-program"><SelectValue placeholder="Optional program assignment" /></SelectTrigger>
                <SelectContent>
                  {PROGRAM_OPTIONS.map(p => <SelectItem key={p.value || '__none'} value={p.value || '__none'}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Agency (for referral source or client) */}
            {showAgency && agencies.length > 0 && (
              <div className="space-y-1.5">
                <Label>Agency / Organization</Label>
                <Select value={form.agency_id} onValueChange={v => setForm(f => ({ ...f, agency_id: v }))}>
                  <SelectTrigger data-testid="select-agency"><SelectValue placeholder="Assign to agency" /></SelectTrigger>
                  <SelectContent>
                    {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.county})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Expiry */}
            <div className="space-y-1.5">
              <Label>Link Expiry</Label>
              <Select value={form.expires_hours} onValueChange={v => setForm(f => ({ ...f, expires_hours: v }))}>
                <SelectTrigger data-testid="select-expiry"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Pre-fill fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Pre-fill Name</Label>
                <Input placeholder="Optional" value={form.pre_fill_name} onChange={e => setForm(f => ({ ...f, pre_fill_name: e.target.value }))} data-testid="prefill-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Pre-fill Email</Label>
                <Input placeholder="Optional" value={form.pre_fill_email} onChange={e => setForm(f => ({ ...f, pre_fill_email: e.target.value }))} data-testid="prefill-email" />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label>Internal Notes</Label>
              <Input placeholder="e.g., Referred via in-person intake" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="link-notes" />
            </div>
          </div>
        )}

        <DialogFooter>
          {createdLink ? (
            <Button onClick={handleClose} data-testid="done-btn">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating || !form.role} data-testid="generate-link-btn">
                {creating ? <Loader2 size={14} className="animate-spin mr-1" /> : <LinkIcon size={14} className="mr-1" />}
                Generate Link
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
